/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, Navigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import TaskModal from './Settings/Modal/TaskModal'
// import BroadcastChannelAPI from '../Components/FocusTimer/BroadcastChannelAPI'
import { AuthContext } from '../AuthProvider'
import PublicRoutes from './PublicRoutes'
import { authentication, fetchBackgroundImages, isMac, verifyShortcut } from '../Components/action/utilities'
import PrivateRoutes from './PrivateRoutes'
import FocusTimerSockets from './FocusTimerSockets'
import { useSocket } from '../SocketContext'
import { decodeToken } from "react-jwt";
import { initFocusTimer } from '../redux/slices/timer'
import { useDispatch } from 'react-redux'
import { setFocusTasksList } from '../redux/slices/TaskSlice'
import { fetchSetting, subscriptionDetails } from '../Components/action/common'
import { setSettings } from '../redux/slices/SettingSclice'
import { setBackgroundImages, setColorPicker, setTheme, setThemeDefaultImage, setDarkThemeDefaultImage, setColorPickerDark } from '../redux/slices/ThemeSlice'
import AiAssistantModal from './Settings/Modal/AiAssistantModal'
import FocusTimerEvents from './FocusTimerEvents'
import { THEME } from '../Components/common/constants'
import GlobalTaskSearchModal from './Settings/Modal/GlobalTaskSearchModal'
import useSystemTheme from "../hooks/useSystemTheme";

function Layout() {
  const focusTimer = useSelector(state =>  state.focusTimer);
  const systemTheme = useSystemTheme();
  const {activeTimer, pause, start, fullscreen} = focusTimer;
  const focusTimerString = JSON.stringify(focusTimer);
  const focusTimerMemo = useMemo(() => JSON.parse(focusTimerString), [focusTimerString]);
  const focusTasksList = useSelector(state => state.task.focusTasksList);
  const focusTasksListString = JSON.stringify(focusTasksList ?? []);
  const focusTasksListMemo = useMemo(() => JSON.parse(focusTasksListString), [focusTasksListString]);
  const { isAuthenticated, setSubscription, redirection } = useContext(AuthContext);
  const location = useLocation();
  const {socket, focusTimerMeta} = useSocket();
  const dispatch = useDispatch();
  const { backgroundImageId, darkBackgroundImageId, backgroundImages, theme, colorPicker, colorPickerDark } = useSelector(state => state.theme);
  const [openTaskModal, setOpenTaskModal] = useState(null);
  const [focusTimerData, setFocusTimerData] = useState(null);
  const settings = useSelector(state => state.settings.settings);
  const appTheme = settings?.theme;

  useEffect(() => {
    fetchSettinValue().then(settings => {
      fetchBackgroundImages().then( (images) => {
        let defaultLightImage = null;
        let defaultDarkImage = null;
        let defaultAccentColor = null;
        let defaultAccentColorDark = null;
        const themeMode = settings?.theme ?? process.env.REACT_APP_DEFAULT_MODE;
    
        // Local background image
        if(settings?.backgroundImageId){
          defaultLightImage = images.find(img => img.id == settings?.backgroundImageId)?.id ?? null;
        }
        if(settings?.darkBackgroundImageId){
          defaultDarkImage = images.find(img => img.id == settings?.darkBackgroundImageId)?.id ?? null;
        }
        // Local background image
        if(settings?.accentColor){
          defaultAccentColor = settings?.accentColor;
        }
        if(settings?.accentColorDark){
          defaultAccentColorDark = settings?.accentColorDark;
        }
        // If local not found then apply global default background image
        if(!defaultLightImage){
          defaultLightImage = (images.find(img => img.isDefault && !img.darkMode))
          ?.id ?? null;
        }
       
        if(!defaultDarkImage){
          defaultDarkImage = (images.find(img => img.isDefault && img.darkMode))
          ?.id ?? null;
        }
        // If local not found then apply global default accent color
        if(!defaultAccentColor){
          defaultAccentColor = (images.find(img => img.isDefault && !img.darkMode))?.defaultColor ?? THEME.DEFAULT_COLOR;
        }
        if(!defaultAccentColorDark){
          defaultAccentColorDark = (images.find(img => img.isDefault && img.darkMode))?.defaultColor ?? THEME.DEFAULT_COLOR;
        }

        dispatch(setBackgroundImages(images));
        defaultLightImage && dispatch(setThemeDefaultImage(defaultLightImage));
        defaultDarkImage && dispatch(setDarkThemeDefaultImage(defaultDarkImage));
        defaultAccentColor && dispatch(setColorPicker(defaultAccentColor));
        defaultAccentColorDark && dispatch(setColorPickerDark(defaultAccentColorDark));
        if(themeMode !== 'system'){
          dispatch(setTheme(themeMode));
        }
      }).catch(e => {});
    }).catch(e => {});
  }, [])

  useEffect(() => {
    document.removeEventListener('keydown', focusInputFieldCmd, false);
    document.addEventListener('keydown', focusInputFieldCmd, false);

    return () => {
      document.removeEventListener('keydown', focusInputFieldCmd, false);
    }
  }, [settings]);

  useEffect(() => {
    if(appTheme == 'system'){
      dispatch(setTheme(systemTheme));
    }
  }, [systemTheme, appTheme])

  useEffect(() => {
    if(theme == 'system'){
      return;
    }
    const tmpTheme = theme;
    const accentColor = tmpTheme === 'dark' ? colorPickerDark : colorPicker;
    document.body.style.setProperty('--accent-color', accentColor);
    document.body.style.setProperty('--accent-color-app', accentColor);
    document.body.style.setProperty('--accent-color-10', `${accentColor}1A`);
    document.body.style.setProperty('--accent-color-app-10', `${accentColor}1A`);
    document.body.style.setProperty('--accent-color-theme', tmpTheme === 'dark' ? `${accentColor}1A` : accentColor);
    document.body.style.setProperty('--color-bg', tmpTheme === 'dark' ? '#000000' : '#ffffff');
    document.body.style.setProperty('--color-text', tmpTheme === 'dark' ? '#ffffff' : '#000000');
    document.body.style.setProperty('--accent-color-reverse', tmpTheme === 'dark' ? '#ffffff' : accentColor);
    document.body.style.setProperty('--fill-none', 'none');
    document.body.style.setProperty('--theme-mode-color-white', tmpTheme === 'dark' ? '#000000' : '#ffffff');
    document.body.style.setProperty('--theme-mode-rgb-white', tmpTheme === 'dark' ? '0, 0, 0' : '255, 255, 255');
    document.body.style.setProperty('--theme-mode-rgb-white-reverse', tmpTheme === 'dark' ? '255, 255, 255' : '0, 0, 0');
    document.body.style.setProperty('--theme-mode-color-white-reverse', tmpTheme === 'dark' ? '#ffffff' : '#000000');
    document.body.style.setProperty('--overDue-color', '#D95555');
    document.body.classList.remove('dark', 'light', 'bg-white', 'bg-black');
    document.body.classList.add(tmpTheme);
    document.body.classList.add(tmpTheme == 'dark' ? 'bg-black' : 'bg-white');
  }, [colorPicker, colorPickerDark, theme])

  useEffect(() => {
    // Check subscription details
    subscriptionDetails().then(data => {
      setSubscription(data)
    }).catch(e => {});
  }, [location.pathname])

  useEffect(() => {
    if(!(isAuthenticated && authentication()) || theme == 'system') 
      return;

    setBackgroundImage(backgroundImageId, darkBackgroundImageId, theme);
  }, [backgroundImageId, darkBackgroundImageId, theme])

  const fetchSettinValue = async () => {
    let settings = {};
    try {
      let response = await fetchSetting();
      settings = response?.data?.responseData?.settings ?? {};
      dispatch(setSettings(settings));
    } catch ({ response }) {}

    return settings;
  }

  const setBackgroundImage = async (id, darkId, theme) => {
    // document.body.style.setProperty('background', `linear-gradient(0deg, rgba(0, 0, 0, 0.03) 0%, rgba(0, 0, 0, 0.03) 100%), linear-gradient(180deg, #FFF 0%, rgba(255, 255, 255, 0.96) 6.22%, rgba(255, 255, 255, 0.70) 100%), ${colorPicker}`);
    const bodyStyle = document.body.style;
    let bgImage = null;
    if(theme == 'dark'){
      bgImage = backgroundImages.find(img => img.id == darkId)
    }else{
      bgImage = backgroundImages.find(img => img.id == id)
    }

    if(!bgImage){
      bgImage = {location: THEME.DEFAULT_IMAGE};
    }

    if(bgImage?.location){
      bodyStyle.backgroundImage = `
        url("${bgImage?.location}"),
        linear-gradient(0deg, rgba(var(--theme-mode-rgb-white-reverse), 0.03) 0%, rgba(var(--theme-mode-rgb-white-reverse), 0.03) 100%),
        linear-gradient(180deg, var(--theme-mode-color-white) 0%, rgba(var(--theme-mode-rgb-white), 0.96) 6.22%, rgba(var(--theme-mode-rgb-white), 0.70) 100%)
      `;
    }else{
      bodyStyle.backgroundImage = `linear-gradient(0deg, rgba(var(--theme-mode-rgb-white-reverse), 0.03) 0%, rgba(var(--theme-mode-rgb-white-reverse), 0.03) 100%),
        linear-gradient(180deg, var(--theme-mode-color-white) 0%, rgba(var(--theme-mode-rgb-white), 0.96) 6.22%, rgba(var(--theme-mode-rgb-white), 0.70) 100%)`;
    }

    bodyStyle.backgroundColor = theme === 'dark' ? colorPickerDark : colorPicker;
    bodyStyle.backgroundPosition = '50%';
    bodyStyle.backgroundSize = 'cover';
    bodyStyle.backgroundRepeat = 'no-repeat';
  }

  const focusInputFieldCmd = (e) => {
    if(typeof settings.keyboardShortcuts == 'undefined' || settings?.keyboardShortcuts?.addTask?.length > 0){
      const shortcut = settings?.keyboardShortcuts?.addTask?.length > 0 ? settings?.keyboardShortcuts?.addTask.join(' ') : (isMac ? 'Cmd' : 'Ctrl') + ' Enter';

      // Check if either meta (Cmd) or ctrl is pressed, and the 'K' key is pressed
      if(verifyShortcut(shortcut, e)){
        focusTaskInputField();
      }
    }
  }

  const focusTaskInputField = () => {
    const path = location.pathname;
    let currentPage = 'home';
    switch (path) {
      case '/tasks':
        currentPage = 'upComing';
        break;
      case '/tasks/overdue':
        currentPage = 'overDue';
        break;
      case '/tasks/inbox':
        currentPage = 'allTask';
        break;
      case '/tasks/nodate':
        currentPage = 'noDate';
        break;
      case '/tasks/complete':
        currentPage = 'completedList';
        break;
      default:
        currentPage = 'home';
    }
    setOpenTaskModal({currentPage});
  }

  useEffect(() => {
      if(socket){
        const handleFocusTimer = (data) => {
            const { action, focusTimerData, focusTasksListData } = data;
            // Stop timer in other opened tabs
            switch (action) {
                case 'fetchFocusTimer':
                  triggerWebSocket({action: 'initTimer', focusTimerData: focusTimerMemo, focusTasksListData: focusTasksListMemo})
                  break;

                case 'initTimer':
                  setFocusTimerData({focusTimerData, focusTasksListData});
                  break;
            }
        }
        socket.off('focusTimerInit', handleFocusTimer);
        socket.on('focusTimerInit', handleFocusTimer);

        // Cleanup function to remove the event listener
        return () => {
          socket.off('focusTimerInit', handleFocusTimer);
        };
      }
  }, [socket, focusTimerMemo, focusTasksListMemo])

  useEffect(() => {
    if(focusTimerData && focusTimerMeta && focusTimerMeta?.clientCount > 1){
      dispatch(initFocusTimer(focusTimerData.focusTimerData));
      dispatch(setFocusTasksList(focusTimerData.focusTasksListData));
    }
  }, [focusTimerData, focusTimerMeta])

  const triggerWebSocket = (data) => {
    // Trigger timer in wepapp and other clients
    if (socket !== null){  
        const user = decodeToken(localStorage.getItem('token'));
        socket.emit('focusTimerInit', {room: user['outseta:accountUid'], data});
    }
  }

  return (
    isAuthenticated && authentication()
    ?
    <div css={{
      '.accent-text-color': {
        color: theme == 'dark' ? colorPickerDark : colorPicker
      }
    }}>
      {
        openTaskModal
        &&
        <TaskModal
            show={true}
            data={null}
            currentPage={openTaskModal.currentPage}
            onHide={() => {
              setOpenTaskModal(null)
            }}
        />
      }
      {/* <BroadcastChannelAPI /> */}
      {redirection && <Navigate to={redirection} />}
      <PrivateRoutes activeTimer={activeTimer} pause={pause} location={location} start={start} fullscreen={fullscreen} />
      {socket && <FocusTimerSockets socket={socket} />}
      {location.pathname !== '/ai-assistant' && <AiAssistantModal fullscreenButton={!(activeTimer === 'focusMode' || activeTimer === 'FocusModePause')} />}
      {location.pathname !== '/tasks' && <GlobalTaskSearchModal />}
      {!(fullscreen || location.pathname === '/focus-mode-settings') && <FocusTimerEvents />}
    </div>
    :
    (
      location.pathname.match(/\/(signup|login|welcome|logout|plan-upgrade)$/) === null
      ?
      <Navigate to="/welcome" />
      :
      <PublicRoutes />
    )
  )
}

export default Layout